import { ProjectState } from './project.reducer';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ProjectSummary } from './models/project-summary.model';
import { getDeploymentSubdomain } from '../user/user.selectors';
import { PermissionsSelectors } from '../user/permissions.selectors';
import { generateDeployLink } from '../config/helpers/generate-deploy-link';
import { ProjectDeploymentStatus } from 'src/app/dashboard/components/dashboard-project-card/enums/project-deployment-status.enum';

export const getProjectState = createFeatureSelector<ProjectState>('project');

export const getCurrentProject = createSelector(
  getProjectState,
  (state: ProjectState) => state.current,
);

export const getCurrentProjectName = createSelector(getCurrentProject, (state) => state?.name);

export const getProjects = createSelector(getProjectState, (state: ProjectState) => state.projects);

export const getProjectSummaries = createSelector(
  getProjectState,
  (state: ProjectState) => state.projectSummaries,
);

export const getProjectSummary = createSelector(
  getProjectState,
  (state: ProjectState) => state.projectSummary,
);

export const getProjectName = createSelector(getProjectSummary, (state) => state?.name);

export const selectPreviewProjectId = createSelector(
  getProjectState,
  (state) => state.previewProjectId,
);

export const getProjectSummaryRequestStatus = createSelector(
  getProjectState,
  (state: ProjectState) => state.requestProjectSummaryInProgress,
);

export const getProject = createSelector(
  getProjectSummaries,
  (projects: ProjectSummary[], props: any): ProjectSummary => {
    const { project } = props;
    return projects?.find((item) => item.id === project.id);
  },
);

export const getDeployable = createSelector(getProject, (project): boolean => {
  if (project) {
    return Boolean(project.configId && project.avatar);
  }
  return false;
});

export const isDeployed = createSelector(
  getProject,
  (project): boolean => project && project.isDeployed,
);

export const hasAvatar = createSelector(getProject, (project): boolean => {
  if (project) {
    return Boolean(project.avatar);
  }
  return;
});

export const getSortOrder = createSelector(getProjectState, (state) => state.sortOrder);

export const getQuery = createSelector(getProjectState, (state) => state.query);

export const getShowAll = createSelector(getQuery, (state) => state.showAll);

export const getProjectApiError = createSelector(getProjectState, (state) => state.apiError);

export const getDeploymentConditions = createSelector(
  getDeployable,
  isDeployed,
  (deployable, deployed) => ({ deployable, deployed }),
);

export const getDeploymentInfo = createSelector(
  getDeploymentConditions,
  getDeploymentSubdomain,
  PermissionsSelectors.selectHasDeployPermission,
  (deploymentConditions, deploymentSubdomain, userHasDeployPermission, props) => {
    const project = props.project as ProjectSummary;
    return (
      project && {
        deployable: deploymentConditions.deployable,
        deployed: deploymentConditions.deployed,
        deploymentStatus: project.isDeployed
          ? ProjectDeploymentStatus.Deployed
          : ProjectDeploymentStatus.NotDeployed,
        deployedLink: generateDeployLink(project.keyName, deploymentSubdomain, project.token),
        hasPublicUrl: deploymentConditions.deployed && project.token,
        deploymentSubdomain,
        userHasDeployPermission,
      }
    );
  },
);

export const getProjectRequestStatus = createSelector(
  getProjectState,
  (state) => state.requestInProgress,
);
